import { useMemo, useState } from "react";
import moment from "moment";
import CustomInput from "../../../../commonComponents/CustomInput";
import CustomDropdown from "../../../../commonComponents/CustomDropdown";
import CustomDatePicker from "../../../../commonComponents/CustomDatePicker";
import CustomTypeAhead from "../../../../commonComponents/CustomTypeAhead";
import ArticleIcon from "../../../../assets/icons/article.svg";
import CheckIcon from "../../../../assets/icons/check.svg";
import CloseIcon from "../../../../assets/icons/close_grey.svg";
import {
  JOB_DETAILS_EMPLOYEE_SEARCH,
  JOB_DETAILS_OPTIONS,
} from "../../../../data/jobDetailsConfig";
import { commonFields } from "../../../../constants/people";
import {
  DATE_OF_JOINING_MAX_FUTURE_DAYS,
  getDateOfJoiningMinDate,
} from "../../../../utils/onboardingHelpers";
import "./JobDetailsSection.css";

const JobDetailsSection = ({
  data = {},
  onChange,
  options: optionsProp,
  onCopyEmployeeDetails,
  isCopyLoading = false,
  isError = false,
  readOnly = false,
}) => {
  const [copyEmployeeId, setCopyEmployeeId] = useState("");
  const [searchList, setSearchList] = useState([]);

  const misc = data?.misc || {};

  const updateField = (fieldKey, value) => {
    if (readOnly) return;
    onChange?.(fieldKey, value);
  };

  const updateMisc = (fieldKey, value) => {
    if (readOnly) return;
    onChange?.("misc", {
      ...misc,
      [fieldKey]: value,
    });
  };

  const onFetchDetails = () => {
    if (readOnly) return;
    const employeeId = String(copyEmployeeId || "").trim();
    if (!employeeId || isCopyLoading) return;
    onCopyEmployeeDetails?.(employeeId);
  };

  const canFetch =
    !readOnly &&
    String(copyEmployeeId || "").trim().length > 0 &&
    !isCopyLoading;

  const filterEmployees = (query) => {
    const q = String(query || "")
      .trim()
      .toLowerCase();
    if (!q) {
      setSearchList([]);
      return;
    }
    setSearchList(
      JOB_DETAILS_EMPLOYEE_SEARCH.filter(
        (item) =>
          String(item.employee_id || "")
            .toLowerCase()
            .includes(q) ||
          String(item.full_name || "")
            .toLowerCase()
            .includes(q),
      ),
    );
  };

  const handleSelect = (item, type) => {
    if (type === "direct") {
      updateField("direct_manager_employee_id", item?.employee_id || "");
      updateField("direct_manager_name", item?.full_name || "");
      updateField("direct_manager_email", item?.company_email_id || "");
    } else if (type === "hrbp") {
      updateField("hrbp_employee_id", item?.employee_id || "");
      updateField("hrbp_name", item?.full_name || "");
      updateField("hrbp_email_id", item?.company_email_id || "");
    }
  };

  const showOptional = true;

  const options = useMemo(
    () => optionsProp || JOB_DETAILS_OPTIONS,
    [optionsProp],
  );

  return (
    <div
      className={`rr-job-details${readOnly ? " is-readonly" : ""}`}
      aria-readonly={readOnly || undefined}
    >
      <div className="rr-job-copy-banner">
        <div className="rr-job-copy-banner__head">
          <img
            className="rr-job-copy-banner__icon"
            src={ArticleIcon}
            alt=""
            aria-hidden="true"
          />
          <div className="rr-job-copy-banner__title">
            Copy from a similar employee?
          </div>
        </div>
        <p className="rr-job-copy-banner__desc">
          If someone already on the roster has the same store and role, enter
          their employee ID and we&apos;ll pull in their department,
          designation, shift, and other job details — no retyping.
          Everything&apos;s editable, so adjust anything that&apos;s different
          before saving.
        </p>
        <div className="rr-job-copy-banner__controls">
          <input
            className="rr-job-copy-banner__input"
            type="text"
            value={copyEmployeeId}
            placeholder="e.g. EMP10231"
            onChange={(event) => {
              if (readOnly) return;
              setCopyEmployeeId(event.target.value);
            }}
            disabled={readOnly}
            readOnly={readOnly}
            aria-label="Similar employee ID"
          />
          <button
            type="button"
            className={`rr-job-copy-banner__fetch${
              canFetch ? "" : " is-disabled"
            }`}
            disabled={!canFetch}
            onClick={onFetchDetails}
          >
            {isCopyLoading ? "Fetching..." : "Fetch details"}
          </button>
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec1">
        <div className="flex flex-wrap">
          <CustomInput
            isError={isError}
            label={"Employee ID"}
            editable={!readOnly}
            value={data?.employee_id}
            placeholder="User Employee ID"
            onChange={(value) => updateField("employee_id", value)}
            isRequired={false}
            preview={showOptional}
            readOnly={true}
          />

          <CustomDatePicker
            disabled={readOnly}
            isRequired={
              commonFields?.user_details?.date_of_joining.required
            }
            showOptional={showOptional}
            isError={isError}
            label={"Date of Joining"}
            onChange={(value) => {
              updateField(
                "date_of_joining",
                value ? moment(value).format("YYYY-MM-DD") : "",
              );
              updateField(
                "date_of_confirmation",
                value
                  ? moment(value).add(6, "months").format("YYYY-MM-DD")
                  : "",
              );
            }}
            value={data?.date_of_joining}
            placeholderText={"Select a date"}
            minAllowedDate={getDateOfJoiningMinDate().toDate()}
            maxAllowedDate={DATE_OF_JOINING_MAX_FUTURE_DAYS}
          />

          <CustomDatePicker
            disabled={readOnly}
            isRequired={
              commonFields?.user_details?.date_of_confirmation.required
            }
            showOptional={showOptional}
            isError={isError}
            label={"Date of Confirmation"}
            onChange={(value) =>
              updateField(
                "date_of_confirmation",
                value ? moment(value).format("YYYY-MM-DD") : "",
              )
            }
            value={data?.date_of_confirmation}
            placeholderText={"Select a date"}
            allowFutureDate={true}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Confirmation Status"}
            isError={isError}
            className="emp-form-input"
            options={options.confirmationStatus}
            value={data?.confirmation_status}
            onSelectOption={(value) =>
              updateField("confirmation_status", value)
            }
            isRequired={
              commonFields?.user_details?.confirmation_status?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Employee Type"}
            isError={isError}
            className="emp-form-input"
            options={options.employeeType}
            value={data?.employee_type}
            onSelectOption={(value) => updateField("employee_type", value)}
            isRequired={commonFields?.user_details?.employee_type?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Employee Sub-Type"}
            isError={isError}
            className="emp-form-input"
            options={options.employeeSubType}
            value={data?.employee_sub_type}
            onSelectOption={(value) => updateField("employee_sub_type", value)}
            isRequired={
              commonFields?.user_details?.employee_sub_type?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Employment Type"}
            isError={isError}
            className="emp-form-input"
            options={options.employmentType}
            value={data?.employment_type}
            onSelectOption={(value) => updateField("employment_type", value)}
            isRequired={
              commonFields?.user_details?.employment_type?.required
            }
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec2">
        <div className="flex flex-wrap">
          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Job Band"}
            isError={isError}
            className="emp-form-input"
            options={options.jobBand}
            value={data?.band}
            onSelectOption={(value) => updateField("band", value)}
            isRequired={commonFields?.user_details?.band?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Job Grade"}
            isError={isError}
            className="emp-form-input"
            options={options.jobGrade}
            value={data?.grade}
            onSelectOption={(value) => updateField("grade", value)}
            isRequired={commonFields?.user_details?.grade?.required}
          />

          <CustomInput
            isError={isError}
            label={"Division"}
            editable={!readOnly}
            value={data?.division}
            placeholder="Enter Division"
            onChange={(value) => updateField("division", value)}
            isRequired={commonFields?.user_details?.division.required}
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Designation Name & Code"}
            isError={isError}
            className="emp-form-input"
            options={options.designationNameAndCode}
            value={data?.designation}
            onSelectOption={(value) => updateField("designation", value)}
            isRequired={commonFields?.user_details?.designation?.required}
          />

          <CustomInput
            isError={isError}
            label={"Position Name & Code"}
            editable={!readOnly}
            value={data?.position_id}
            placeholder="Enter Position Name & Code"
            onChange={(value) => updateField("position_id", value)}
            isRequired={commonFields?.user_details?.position_id.required}
            preview={showOptional}
            // Required-only: no format/regex validation in Review Request
            validationStr={/^[\s\S]*$/}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Job Level & Code"}
            isError={isError}
            className="emp-form-input"
            options={options.jobLevelAndCode}
            value={data?.job_level}
            isObjectDrop={false}
            keyName={"value"}
            previewKey={true}
            onSelectOption={(value) => updateField("job_level", value)}
            isRequired={commonFields?.user_details?.job_level?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Business Unit"}
            isError={isError}
            className="emp-form-input"
            options={options.businessUnit}
            value={data?.business_unit}
            onSelectOption={(value) => updateField("business_unit", value)}
            isRequired={commonFields?.user_details?.business_unit?.required}
            keyName={"value"}
            isObjectDrop={false}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Business Unit Head Name"}
            isError={isError}
            className="emp-form-input"
            options={options.businessUnitHeadName}
            value={data?.business_unit_head}
            onSelectOption={(value) =>
              updateField("business_unit_head", value)
            }
            isRequired={
              commonFields?.user_details?.business_unit_head?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Department Code"}
            isError={isError}
            className="emp-form-input"
            options={options.departmentCode}
            value={data?.department_code}
            onSelectOption={(value) => updateField("department_code", value)}
            isRequired={commonFields?.user_details?.department_code?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Department Hierarchy"}
            isError={isError}
            className="emp-form-input"
            options={options.departmentHierarchy}
            value={data?.departments_hierarchy}
            onSelectOption={(value) =>
              updateField("departments_hierarchy", value)
            }
            isRequired={
              commonFields?.user_details?.departments_hierarchy?.required
            }
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec3">
        <div className="flex flex-wrap">
          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Group Company & Code"}
            isError={isError}
            className="emp-form-input"
            options={options.groupCompanyCode}
            value={data?.group_company_code}
            onSelectOption={(value) =>
              updateField("group_company_code", value)
            }
            isRequired={
              commonFields?.user_details?.group_company_code?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Base Office Location"}
            isError={isError}
            className="emp-form-input"
            options={options.baseOfficeLocation}
            value={data?.base_office_location}
            onSelectOption={(value) =>
              updateField("base_office_location", value)
            }
            isRequired={
              commonFields?.user_details?.base_office_location?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Location Type"}
            isError={isError}
            className="emp-form-input"
            options={options.locTypeList}
            value={data?.location_type}
            onSelectOption={(value) => updateField("location_type", value)}
            isRequired={commonFields?.user_details?.location_type?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Office Country"}
            isError={isError}
            className="emp-form-input"
            options={options.officeCountry}
            value={data?.office_country}
            onSelectOption={(value) => updateField("office_country", value)}
            isRequired={commonFields?.user_details?.office_country?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Office State"}
            isError={isError}
            className="emp-form-input"
            options={options.officeState}
            value={data?.office_state}
            onSelectOption={(value) => updateField("office_state", value)}
            isRequired={commonFields?.user_details?.office_state?.required}
          />

          <CustomInput
            isError={isError}
            label={"Office Region"}
            editable={!readOnly}
            value={data?.office_region}
            placeholder="Enter Office Region"
            onChange={(value) => updateField("office_region", value)}
            isRequired={commonFields?.user_details?.office_region.required}
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Office City"}
            isError={isError}
            className="emp-form-input"
            options={options.officeCity}
            value={data?.office_city}
            onSelectOption={(value) => updateField("office_city", value)}
            isRequired={commonFields?.user_details?.office_city?.required}
          />

          <CustomInput
            isError={isError}
            label={"City Type"}
            editable={!readOnly}
            value={data?.city_type}
            placeholder="Enter City Type"
            onChange={(value) => updateField("city_type", value)}
            isRequired={commonFields?.user_details?.city_type.required}
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Cost Center"}
            isError={isError}
            className="emp-form-input"
            options={options.costCenter}
            value={data?.cost_center}
            onSelectOption={(value) => updateField("cost_center", value)}
            isRequired={commonFields?.user_details?.cost_center?.required}
          />

          <CustomInput
            isError={isError}
            label={"Centre Type"}
            editable={!readOnly}
            value={data?.centre_type}
            placeholder="Enter Centre Type"
            onChange={(value) => updateField("centre_type", value)}
            isRequired={commonFields?.user_details?.centre_type.required}
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Work Area Code"}
            isError={isError}
            className="emp-form-input"
            options={options.location_fin}
            value={data?.work_area_code_x}
            onSelectOption={(value) => updateField("work_area_code_x", value)}
            isRequired={commonFields?.user_details?.work_area_code_x?.required}
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec4">
        <div className="flex flex-wrap">
          <CustomTypeAhead
            labelKey="Direct Manager Employee ID*"
            data={searchList}
            onChange={(value) => {
              updateField("direct_manager_employee_id", value);
              if (value.length <= 6) {
                updateField("direct_manager_name", "");
                updateField("direct_manager_email", "");
              }
            }}
            onSearch={(value) => {
              if (value.length <= 3) {
                updateField("direct_manager_name", "");
                updateField("direct_manager_email", "");
              }
              filterEmployees(value);
            }}
            onSelect={(item) => handleSelect(item, "direct")}
            required={
              commonFields?.user_details?.direct_manager_employee_id.required
            }
            minLength={3}
            isEmployeeSearch={true}
            valueLabelKey="full_name"
            placeholder="Search using employee id or name"
            isError={isError}
            value={data?.direct_manager_employee_id}
          />
          <CustomInput
            isError={isError}
            label={"Direct Manager Name"}
            editable={!readOnly}
            value={data?.direct_manager_name}
            placeholder="Direct Manager Name"
            onChange={(value) => updateField("direct_manager_name", value)}
            isRequired={
              commonFields?.user_details?.direct_manager_name.required
            }
            preview={showOptional}
            // Required-only: no format/regex validation in Review Request
            validationStr={/^[\s\S]*$/}
          />
          <CustomInput
            isError={isError}
            label={"Direct Manager Email"}
            editable={!readOnly}
            value={data?.direct_manager_email}
            placeholder="Enter Direct Manager Email"
            onChange={(value) => updateField("direct_manager_email", value)}
            isRequired={
              commonFields?.user_details?.direct_manager_email.required
            }
            preview={showOptional}
            // Required-only: no format/regex validation in Review Request
            validationStr={/^[\s\S]*$/}
          />

          <CustomTypeAhead
            labelKey="HRBP Employee ID*"
            data={searchList}
            onChange={(value) => {
              updateField("hrbp_employee_id", value);
              if (value.length <= 6) {
                updateField("hrbp_name", "");
                updateField("hrbp_email_id", "");
              }
            }}
            onSearch={(value) => {
              if (value.length <= 3) {
                updateField("hrbp_name", "");
                updateField("hrbp_email_id", "");
              }
              filterEmployees(value);
            }}
            onSelect={(item) => handleSelect(item, "hrbp")}
            required={commonFields?.user_details?.hrbp_employee_id.required}
            minLength={3}
            isEmployeeSearch={true}
            valueLabelKey="full_name"
            placeholder="Search using employee id or name"
            isError={isError}
            value={data?.hrbp_employee_id}
          />
          <CustomInput
            isError={isError}
            label={"HRBP Name"}
            editable={!readOnly}
            value={data?.hrbp_name}
            placeholder="HRBP Name"
            onChange={(value) => updateField("hrbp_name", value)}
            isRequired={commonFields?.user_details?.hrbp_name.required}
            preview={showOptional}
            // Required-only: no format/regex validation in Review Request
            validationStr={/^[\s\S]*$/}
          />
          <CustomInput
            isError={isError}
            label={"HRBP Email ID"}
            editable={!readOnly}
            value={data?.hrbp_email_id}
            placeholder="Enter HRBP Email ID"
            onChange={(value) => updateField("hrbp_email_id", value)}
            isRequired={commonFields?.user_details?.hrbp_email_id.required}
            preview={showOptional}
            // Required-only: no format/regex validation in Review Request
            validationStr={/^[\s\S]*$/}
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec5">
        <div className="flex flex-wrap">
          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Time Zone"}
            isError={isError}
            className="emp-form-input"
            options={options.timeZone}
            value={data?.time_zone}
            onSelectOption={(value) => updateField("time_zone", value)}
            isRequired={commonFields?.user_details?.time_zone?.required}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Attendance Shift"}
            isError={isError}
            className="emp-form-input"
            options={options.attendanceShift}
            value={data?.attendance_shift}
            onSelectOption={(value) => updateField("attendance_shift", value)}
            isRequired={
              commonFields?.user_details?.attendance_shift?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Attendance Policy"}
            isError={isError}
            className="emp-form-input"
            options={options.attendancePolicy}
            value={data?.attendance_policy}
            onSelectOption={(value) => updateField("attendance_policy", value)}
            isRequired={
              commonFields?.user_details?.attendance_policy?.required
            }
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Attendance Weekly Off"}
            isError={isError}
            className="emp-form-input"
            options={options.attendanceWeeklyOff}
            value={data?.attendance_weeklyoff}
            onSelectOption={(value) =>
              updateField("attendance_weeklyoff", value)
            }
            isRequired={
              commonFields?.user_details?.attendance_weeklyoff?.required
            }
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec6">
        <div className="flex flex-wrap">
          <CustomInput
            isError={isError}
            label={"Contribution Level"}
            editable={!readOnly}
            value={data?.contribution_level}
            placeholder="Enter Contribution Level"
            onChange={(value) => updateField("contribution_level", value)}
            isRequired={
              commonFields?.user_details?.contribution_level.required
            }
            preview={showOptional}
          />
          <CustomInput
            isError={isError}
            label={"Functional Area"}
            editable={!readOnly}
            value={data?.functional_area}
            placeholder="Enter Functional Area"
            onChange={(value) => updateField("functional_area", value)}
            isRequired={commonFields?.user_details?.functional_area.required}
            preview={showOptional}
          />
          <CustomInput
            isError={isError}
            label={"Functional Area Cost Center & Code"}
            editable={!readOnly}
            value={data?.functional_area_cost_center}
            placeholder="Enter Functional Area Cost Center & Code"
            onChange={(value) =>
              updateField("functional_area_cost_center", value)
            }
            isRequired={
              commonFields?.user_details?.functional_area_cost_center.required
            }
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Vendor Name"}
            isError={isError}
            className="emp-form-input"
            options={options.vendorName}
            value={data?.vendor_name}
            onSelectOption={(value) => updateField("vendor_name", value)}
            isRequired={commonFields?.user_details?.vendor_name?.required}
          />

          <CustomInput
            isError={isError}
            label={"Vendor Employee ID"}
            editable={!readOnly}
            value={data?.vendor_emp_id}
            placeholder="Enter Vendor Employee ID"
            onChange={(value) => updateField("vendor_emp_id", value)}
            isRequired={commonFields?.user_details?.vendor_emp_id.required}
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Bill Amazon"}
            isError={isError}
            className="emp-form-input"
            options={options.billAmazon}
            value={data?.bill_amazon}
            placeholder="Select the percentage from range"
            onSelectOption={(value) =>
              updateField(
                "bill_amazon",
                value && typeof value === "object" ? value.value : value,
              )
            }
            isObjectDrop={true}
            keyName="value"
            previewKey={true}
            isRequired={commonFields?.user_details?.bill_amazon?.required}
          />
          <CustomInput
            isError={isError}
            label={"Workforce Engagement"}
            editable={!readOnly}
            value={data?.workforce_engagement}
            placeholder="Enter Workforce Engagement"
            onChange={(value) => updateField("workforce_engagement", value)}
            isRequired={
              commonFields?.user_details?.workforce_engagement.required
            }
            preview={showOptional}
          />

          <CustomDropdown
            disabled={readOnly}
            showOptional={showOptional}
            label={"Channel"}
            isError={isError}
            className="emp-form-input"
            options={options.channel}
            value={data?.channel}
            placeholder="Select the channel"
            onSelectOption={(value) =>
              updateField(
                "channel",
                value && typeof value === "object" ? value.value : value,
              )
            }
            isObjectDrop={true}
            keyName="value"
            previewKey={true}
            isRequired={commonFields?.user_details?.channel?.required}
          />
        </div>
      </div>

      <div className="job-sec" id="rr-job-sec7">
        <div className="flex flex-wrap rr-job-misc-row">
          <CustomInput
            isError={isError}
            label={"Source of job application"}
            editable={!readOnly}
            value={misc?.source_of_job_application}
            placeholder="Enter Source of job application"
            onChange={(value) =>
              updateMisc("source_of_job_application", value)
            }
            isRequired={
              commonFields?.user_details?.source_of_job_application.required
            }
            preview={showOptional}
          />

          <div className="flex flex-col work-check-common ml-12">
            <div className="work-label">
              Ever worked for Aditya Birla Retail or More Retail Pvt Ltd?
            </div>
            <div className="flex pb-12">
              <div
                className={`flex items-center work-check-box ${
                  misc?.previous_worked_for_more ? "border-2" : ""
                }`}
                onClick={() => updateMisc("previous_worked_for_more", true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    updateMisc("previous_worked_for_more", true);
                  }
                }}
              >
                <img className="work-icon" src={CheckIcon} alt="" />
                <div className="work-check-label">Yes</div>
              </div>
              <div
                className={`flex items-center work-check-box ${
                  !misc?.previous_worked_for_more ? "border-2" : ""
                }`}
                onClick={() => updateMisc("previous_worked_for_more", false)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    updateMisc("previous_worked_for_more", false);
                  }
                }}
              >
                <img className="work-icon" src={CloseIcon} alt="" />
                <div className="work-check-label">No</div>
              </div>
            </div>
            {misc?.previous_worked_for_more ? (
              <div className="flex flex-col ad-work-detail">
                <CustomInput
                  isError={isError}
                  label={"Previous Employee Code"}
                  editable={!readOnly}
                  value={misc?.previousEmpCode}
                  placeholder="Enter Previous Employee Code"
                  onChange={(value) => updateMisc("previousEmpCode", value)}
                  isRequired={
                    commonFields?.user_details?.previousEmpCode.required
                  }
                  preview={showOptional}
                  styles={{ width: "367px" }}
                />
                <div>
                  <CustomInput
                    isError={isError}
                    label={"Previous Email Address"}
                    editable={!readOnly}
                    value={misc?.previousEmailID}
                    placeholder="Enter Previous Email Address"
                    onChange={(value) => updateMisc("previousEmailID", value)}
                    isRequired={
                      commonFields?.user_details?.previousEmailID.required
                    }
                    preview={showOptional}
                    styles={{ width: "367px" }}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex flex-col work-check-common ml-12">
            <div className="work-label">
              Any Family Members working with More Retail Private Limited?*
            </div>
            <div className="flex pb-12">
              <div
                className={`flex items-center work-check-box ${
                  misc?.family_in_more ? "border-2" : ""
                }`}
                onClick={() => updateMisc("family_in_more", true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    updateMisc("family_in_more", true);
                  }
                }}
              >
                <img className="work-icon" src={CheckIcon} alt="" />
                <div className="work-check-label">Yes</div>
              </div>
              <div
                className={`flex items-center work-check-box ${
                  !misc?.family_in_more ? "border-2" : ""
                }`}
                onClick={() => updateMisc("family_in_more", false)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    updateMisc("family_in_more", false);
                  }
                }}
              >
                <img className="work-icon" src={CloseIcon} alt="" />
                <div className="work-check-label">No</div>
              </div>
            </div>
            {misc?.family_in_more ? (
              <div>
                <CustomInput
                  isError={isError}
                  label={
                    " Name, Official Email ID, Phone Number & Relationship Info"
                  }
                  editable={!readOnly}
                  value={misc?.more_user_details}
                  placeholder="Write here..."
                  onChange={(value) => updateMisc("more_user_details", value)}
                  isRequired={
                    commonFields?.user_details?.more_user_details.required
                  }
                  preview={showOptional}
                  isTextArea={true}
                  styles={{ width: "367px" }}
                  rows={5}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsSection;
